import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import { TableContainer, TableFooter, Theme } from '@mui/material';
import { MRT_TableInstance, MRT_TableBody as MRTTableBody, MRT_TableHead as MRTTableHead } from 'material-react-table';
import { ReactNode } from 'react';

// Extract common theme constants to reduce duplication
const COLORS = {
  BORDER: '#DFDFDF',
  HEADER_BG: '#F5F6FA',
  SELECTED_ROW: 'rgba(0, 153, 43, 0.12)',
  TEXT: '#000000',
};

const BREAKPOINT = 1520;

const FONT_SIZES = {
  DEFAULT: '14px',
  SMALL: '12px',
};

const PADDING = {
  HEADER: {
    DEFAULT: '8px 16px',
    SMALL: '6px 12px',
  },
  CELL: {
    DEFAULT: '12px 16px',
    SMALL: '10px 12px',
  },
};

// Helper function to generate responsive styles with breakpoints
const withResponsive = (defaultValue: any, smallValue: any) => (theme: Theme) => ({
  [defaultValue]: defaultValue,
  [theme.breakpoints.down(BREAKPOINT)]: {
    [defaultValue]: smallValue,
  },
});

// Helper function to generate common cell styling
const createCellStyle =
  (isHeader = false) =>
  (theme: Theme) => ({
    borderRight: `1px solid ${COLORS.BORDER}`,
    fontSize: FONT_SIZES.DEFAULT,
    padding: isHeader ? PADDING.HEADER.DEFAULT : PADDING.CELL.DEFAULT,
    ...(isHeader && { fontWeight: 'bold', backgroundColor: COLORS.HEADER_BG }),
    [theme.breakpoints.down(BREAKPOINT)]: {
      fontSize: FONT_SIZES.SMALL,
      padding: isHeader ? PADDING.HEADER.SMALL : PADDING.CELL.SMALL,
    },
    '&:last-child': {
      borderRight: 0,
    },
  });

// Common table styling to be reused across all tables
export const TableWrapper = styled(Paper)({
  borderRadius: '0',
  boxShadow: 'none',
  border: 'none',
  borderTop: `1px solid ${COLORS.BORDER}`,
  '&::-webkit-scrollbar': {
    height: '12px',
    width: '12px',
    backgroundColor: '#f1f1f1',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#BDBDBD',
    borderRadius: '6px',
    border: '3px solid #f1f1f1',
    backgroundClip: 'content-box',
    '&:hover': {
      backgroundColor: '#999999',
    },
  },
});

export const StyledTable = styled(Table)(({ theme }) => ({
  // Common styles for header and footer cells
  '& .MuiTableCell-head, & .MuiTableCell-footer': createCellStyle(true)(theme),

  // Selected row styling
  '& .MuiTableRow-root.Mui-selected': {
    backgroundColor: `${COLORS.SELECTED_ROW} !important`,
  },

  // Header-specific styling
  '& .MuiTableCell-head': {
    padding: PADDING.HEADER.DEFAULT,
    [theme.breakpoints.down(BREAKPOINT)]: {
      padding: PADDING.HEADER.SMALL,
    },
  },

  '& .Mui-TableHeadCell-Content-Labels': {
    width: '100%',
  },
  '& .Mui-TableHeadCell-Content-Wrapper': {
    width: '100%',
  },

  '& .MuiTableHead-root': {
    top: 0,
  },

  // Footer-specific styling
  '& .MuiTableCell-footer': {
    padding: PADDING.CELL.DEFAULT,
    color: COLORS.TEXT,
    [theme.breakpoints.down(BREAKPOINT)]: {
      padding: PADDING.CELL.SMALL,
    },
  },

  // Body cell styling
  '& .MuiTableCell-body': {
    borderRight: `1px solid ${COLORS.BORDER}`,
    borderBottom: `1px solid ${COLORS.BORDER}`,
    padding: PADDING.CELL.DEFAULT,
    fontSize: FONT_SIZES.DEFAULT,
    [theme.breakpoints.down(BREAKPOINT)]: {
      padding: PADDING.CELL.SMALL,
      fontSize: FONT_SIZES.SMALL,
    },
    '&:last-child': {
      borderRight: 0,
    },
  },

  // Typography in table body
  '& .MuiTableBody-root': {
    // General input styling
    '& .MuiInputBase-root': {
      fontSize: FONT_SIZES.SMALL,
      [theme.breakpoints.up(BREAKPOINT)]: {
        fontSize: FONT_SIZES.DEFAULT,
      },
    },
    // Specific TextField input styling
    '& .MuiTextField-root .MuiInputBase-input': {
      fontSize: `${FONT_SIZES.SMALL} !important`,
      [theme.breakpoints.up(BREAKPOINT)]: {
        fontSize: `${FONT_SIZES.DEFAULT} !important`,
      },
    },
    // Form control input styling
    '& .MuiFormControl-root .MuiInputBase-input': {
      fontSize: `${FONT_SIZES.SMALL} !important`,
      [theme.breakpoints.up(BREAKPOINT)]: {
        fontSize: `${FONT_SIZES.DEFAULT} !important`,
      },
    },
    // Select input styling
    '& .MuiSelect-select': {
      fontSize: `${FONT_SIZES.SMALL} !important`,
      [theme.breakpoints.up(BREAKPOINT)]: {
        fontSize: `${FONT_SIZES.DEFAULT} !important`,
      },
    },
    '& .MuiTypography-root': {
      fontSize: FONT_SIZES.DEFAULT,
      [theme.breakpoints.down(BREAKPOINT)]: {
        fontSize: FONT_SIZES.SMALL,
      },
    },
  },

  // MenuItem styling for dropdown options (including global scope)
  '& .MuiButtonBase-root.MuiMenuItem-root': {
    fontSize: `${FONT_SIZES.SMALL} !important`,
    [theme.breakpoints.up(BREAKPOINT)]: {
      fontSize: `${FONT_SIZES.DEFAULT} !important`,
    },
  },
}));

export interface BaseTableMainProps<T> {
  table: MRT_TableInstance<T>;
  footer?: ReactNode;
  tableContainerProps?: Record<string, any>;
}

// Generic table component that can be used by all tables
export function TableMain<T>({ table, footer, tableContainerProps = {} }: BaseTableMainProps<T>) {
  return (
    <TableContainer component={TableWrapper} {...tableContainerProps}>
      <StyledTable>
        <MRTTableHead table={table} />
        <MRTTableBody table={table} />
        {footer && <TableFooter>{footer}</TableFooter>}
      </StyledTable>
    </TableContainer>
  );
}
